import SeriesLineIconSvg from '@/assets/svg/SeriesLineIconSvg';
import ReportChartSvg from '@/assets/svg/ReportChartSvg';
import Icon, { InfoCircleOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { Checkbox, Tooltip } from '@hydrodigital/design-system';

interface SourceItemProps {
	title: string;
	sourceLineColor: string;
	tooltip: string;
	source: any;
	isLast: boolean;
	checked?: boolean;
	onCheck?: (checked: boolean, sourceId: number, serialNumber: string) => void;
	enabledAnnotation?: boolean;
	onAddAnnotation?: (id: number) => void;
	disabled?: boolean;
	resetAnnotationIcon?: boolean;
	onAnnotationToggle?: (isActive: boolean) => void;
	allItemsUnchecked?: boolean;
	sourceKey?: string;
	activeAnnotationIcon?: string | null;
}

const SourceItem: React.FC<SourceItemProps> = ({
	title,
	sourceLineColor,
	tooltip,
	source,
	isLast,
	checked = false,
	onCheck = () => {},
	enabledAnnotation = false,
	onAddAnnotation = () => {},
	disabled = false,
	resetAnnotationIcon = false,
	onAnnotationToggle = () => {},
	allItemsUnchecked = false,
	sourceKey = '',
	activeAnnotationIcon = null,
}) => {
	const [checkedItem, setCheckedItem] = useState(checked);
	const [annotationIconClicked, setAnnotationIconClicked] = useState(false);

	// Sync local state with prop changes
	useEffect(() => {
		setCheckedItem(checked);
	}, [checked]);

	// Reset annotation icon color when resetAnnotationIcon prop changes
	useEffect(() => {
		if (resetAnnotationIcon) {
			setAnnotationIconClicked(false);
		}
	}, [resetAnnotationIcon]);

	// Sync annotation icon state with global active annotation icon
	useEffect(() => {
		const isThisIconActive = activeAnnotationIcon === sourceKey;
		setAnnotationIconClicked(isThisIconActive);
	}, [activeAnnotationIcon, sourceKey]);

	const handleAnnotationClick = () => {
		if (annotationIconClicked) {
			// If already clicked, reset to default state
			setAnnotationIconClicked(false);
			onAnnotationToggle(false);
		} else {
			// If not clicked, activate and call onAddAnnotation
			setAnnotationIconClicked(true);
			onAddAnnotation(source);
			onAnnotationToggle(true);
		}
	};

	return (
		<div
			className={`flex justify-start items-center gap-2 px-2 ${!isLast ? 'border-r-2 border-[#E5E7EB]' : ''}`}
		>
			<div className="h-4 flex justify-start items-center gap-2">
				<div className="w-4 h-4 p-2 inline-flex flex-col justify-center items-center">
					<Checkbox
						checked={checkedItem}
						onChecked={(checked) => {
							setCheckedItem(checked);
							onCheck(checked, source.sourceId, source.serialNumber);
						}}
						disabled={disabled}
					/>
				</div>
				<div className="flex justify-start items-center gap-1">
					<div className="justify-center text-text-input-primary-active text-xs font-normal leading-none">
						{title}
					</div>
				</div>
			</div>
			<div className="flex justify-center items-center gap-2 mr-2">
				<SeriesLineIconSvg color={sourceLineColor} />
			</div>
			<div className="w-4 h-4 flex justify-center items-center gap-2">
				{enabledAnnotation && checkedItem && source.showAnnotationIcon && (
					<Tooltip position='up' trigger="hover" text={'Add a Quality Annotation to adjust a data source’s quality score and record why. Drag the mouse over the chart to create one.'}>
						<span>
							<Icon
							component={(props: any) => (
								<ReportChartSvg {...props} color={annotationIconClicked ? '#078cb3' : '#74788D'} />
							)}
									onClick={handleAnnotationClick}
								data-annotation-icon="true"
							/>
						</span>
					</Tooltip>
				)}
				<Tooltip position='up' trigger="hover" text={tooltip}>
					<span>
						<InfoCircleOutlined />
					</span>
				</Tooltip>
			</div>
		</div>
	);	2	
};

export default SourceItem;
